import React, { useState, useEffect } from 'react';
import { ArrowLeft, TrendingUp } from 'lucide-react';
import { useCalculator } from '../context/CalculatorContext';
import { compoundMultiplierContent } from '../data/compoundMultiplierContent';
import { motion } from 'framer-motion';
import { getLanguageContent, getLanguageFromCurrency } from '../data/languages';
import CurrencySelector from './CurrencySelector';
import ReinvestmentCentralData from './reinvestmentCalculator/ReinvestmentCentralData';
import ReinvestmentSpecificData from './reinvestmentCalculator/ReinvestmentSpecificData';
import ReinvestmentWithdrawalPlan from './reinvestmentCalculator/ReinvestmentWithdrawalPlan';
import ReinvestmentImpact from './reinvestmentCalculator/ReinvestmentImpact';
import ReinvestmentResults from './reinvestmentCalculator/ReinvestmentResults';
import InvestmentAdvantages from './InvestmentAdvantages';
import ReinvestmentClientInfo from './reinvestmentCalculator/ReinvestmentClientInfo';
import ReinvestmentInvestorGoals from './reinvestmentCalculator/ReinvestmentInvestorGoals';

interface ReinvestmentCalculatorProps {
  onBack: () => void;
}

const ReinvestmentCalculator: React.FC<ReinvestmentCalculatorProps> = ({ onBack }) => {
  const { updateInvestment } = useCalculator();
  const { investment } = useCalculator();
  const [activeStep, setActiveStep] = useState<'currency' | 'investorGoals' | 'clientInfo' | 'centralData' | 'specificData' | 'withdrawalPlan' | 'results' | 'impact' | 'advantages'>('currency');
  
  // Get current language content
  const currentLanguage = investment.language || getLanguageFromCurrency(investment.currencyFormat);
  const content = getLanguageContent(currentLanguage);

  // Set fixed parameters for reinvestment view
  useEffect(() => {
    updateInvestment({
      reinvestProfits: true,
      additionalContributions: false,
      isLongTermCalculator: false,
      firstYearUtilityToUser: false
    });
  }, []); // Remove updateInvestment from dependencies to prevent infinite loop

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeStep]);

  const handleNextStep = () => {
    switch (activeStep) {
      case 'currency':
        setActiveStep('investorGoals');
        break;
      case 'investorGoals':
        setActiveStep('clientInfo');
        break;
      case 'clientInfo':
        setActiveStep('centralData');
        break;
      case 'centralData':
        setActiveStep('specificData');
        break;
      case 'specificData':
        setActiveStep('withdrawalPlan');
        break;
      case 'withdrawalPlan':
        setActiveStep('results');
        break;
      case 'results':
        setActiveStep('impact');
        break;
      case 'impact':
        setActiveStep('advantages');
        break;
      default:
        break;
    }
  };

  const handlePreviousStep = () => {
    switch (activeStep) {
      case 'centralData':
        setActiveStep('clientInfo');
        break;
      case 'clientInfo':
        setActiveStep('investorGoals');
        break;
      case 'investorGoals':
        setActiveStep('clientInfo');
        break;
      case 'investorGoals':
        setActiveStep('currency');
        break;
      case 'specificData':
        setActiveStep('centralData');
        break;
      case 'withdrawalPlan':
        setActiveStep('specificData');
        break;
      case 'results':
        setActiveStep('withdrawalPlan');
        break;
      case 'impact':
        setActiveStep('results');
        break;
      case 'advantages':
        setActiveStep('impact');
        break;
      default:
        break;
    }
  };

  const handleReset = () => {
    setActiveStep('currency');
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 'currency':
        return <CurrencySelector onNext={handleNextStep} />;
      case 'investorGoals':
        return <ReinvestmentInvestorGoals onNext={handleNextStep} onPrevious={handlePreviousStep} />;
      case 'clientInfo':
        return <ReinvestmentClientInfo onNext={handleNextStep} onPrevious={handlePreviousStep} />;
      case 'centralData':
        return <ReinvestmentCentralData onNext={handleNextStep} onPrevious={handlePreviousStep} />;
      case 'specificData':
        return <ReinvestmentSpecificData onNext={handleNextStep} onPrevious={handlePreviousStep} />;
      case 'withdrawalPlan':
        return <ReinvestmentWithdrawalPlan onNext={handleNextStep} onPrevious={handlePreviousStep} />;
      case 'results':
        return <ReinvestmentResults onNext={handleNextStep} onPrevious={handlePreviousStep} onReset={handleReset} />;
      case 'impact':
        return <ReinvestmentImpact onNext={handleNextStep} onPrevious={handlePreviousStep} />;
      case 'advantages':
        return <InvestmentAdvantages onNext={handleNextStep} onPrevious={handlePreviousStep} />;
      default:
        return <CurrencySelector onNext={handleNextStep} />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm shadow-sm border-b border-white/20">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center gap-6">
            <button
              onClick={onBack}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100/50 rounded-xl transition-all duration-200"
              title="Volver al menú principal"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Volver</span>
            </button>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{content.reinvestment.title}</h1>
                <h2 className="text-lg font-semibold text-green-700">{compoundMultiplierContent.title}</h2>
                <p className="text-gray-600">{compoundMultiplierContent.tagline}</p>
                
                {/* Disclaimer */}
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-xs text-yellow-800">
                    <strong>⚠️ Aviso:</strong> Las proyecciones mostradas son estimaciones y no garantizan rendimientos futuros.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {/* Step 0 - Currency Selection */}
              <button 
                onClick={() => setActiveStep('currency')}
                className={`flex flex-col items-center transition-all duration-200 hover:scale-105 ${activeStep === 'currency' ? 'text-blue-600' : (activeStep !== 'currency' && activeStep !== 'investorGoals' && activeStep !== 'clientInfo') ? 'text-blue-400' : 'text-gray-400'}`}
                title="Paso 0: Selección de Moneda"
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 ${
                  activeStep === 'currency' ? 'bg-blue-100 text-blue-600 ring-2 ring-blue-500' : 
                  (activeStep !== 'currency' && activeStep !== 'investorGoals' && activeStep !== 'clientInfo') ? 'bg-blue-50 text-blue-500' : 'bg-gray-100'
                }`}>
                  <span className="text-sm font-bold">0</span>
                </div>
                <span className="text-xs mt-1">Moneda</span>
              </button>
              
              {/* Connector Line 0-0.25 */}
              <div className={`h-0.5 w-8 mx-1 ${(activeStep !== 'currency' && activeStep !== 'investorGoals' && activeStep !== 'clientInfo') ? 'bg-blue-500' : 'bg-gray-200'}`}></div>
              
              {/* Step 0.25 - Investor Goals */}
              <button 
                onClick={() => setActiveStep('investorGoals')}
                className={`flex flex-col items-center transition-all duration-200 hover:scale-105 ${activeStep === 'investorGoals' ? 'text-blue-600' : (activeStep !== 'currency' && activeStep !== 'investorGoals' && activeStep !== 'clientInfo') ? 'text-blue-400' : 'text-gray-400'}`}
                title="Paso 0.25: Meta del Inversionista"
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 ${
                  activeStep === 'investorGoals' ? 'bg-blue-100 text-blue-600 ring-2 ring-blue-500' : 
                  (activeStep !== 'currency' && activeStep !== 'investorGoals' && activeStep !== 'clientInfo') ? 'bg-blue-50 text-blue-500' : 'bg-gray-100'
                }`}>
                  <span className="text-sm font-bold">¼</span>
                </div>
                <span className="text-xs mt-1">Meta</span>
              </button>
              
              {/* Connector Line 0.25-0.5 */}
              <div className={`h-0.5 w-8 mx-1 ${(activeStep !== 'currency' && activeStep !== 'investorGoals' && activeStep !== 'clientInfo') ? 'bg-blue-500' : 'bg-gray-200'}`}></div>
              
              {/* Step 0.5 - Client Info */}
              <button 
                onClick={() => setActiveStep('clientInfo')}
                className={`flex flex-col items-center transition-all duration-200 hover:scale-105 ${activeStep === 'clientInfo' ? 'text-blue-600' : (activeStep !== 'currency' && activeStep !== 'investorGoals' && activeStep !== 'clientInfo') ? 'text-blue-400' : 'text-gray-400'}`}
                title="Paso 0.5: Información del Cliente"
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 ${
                  activeStep === 'clientInfo' ? 'bg-blue-100 text-blue-600 ring-2 ring-blue-500' : 
                  (activeStep !== 'currency' && activeStep !== 'investorGoals' && activeStep !== 'clientInfo') ? 'bg-blue-50 text-blue-500' : 'bg-gray-100'
                }`}>
                  <span className="text-sm font-bold">½</span>
                </div>
                <span className="text-xs mt-1">Cliente</span>
              </button>
              
              {/* Connector Line 0.5-1 */}
              <div className={`h-0.5 w-8 mx-1 ${(activeStep !== 'currency' && activeStep !== 'investorGoals' && activeStep !== 'clientInfo') ? 'bg-blue-500' : 'bg-gray-200'}`}></div>
              
              {/* Step 1 - Datos Centrales */}
              <button 
                onClick={() => setActiveStep('centralData')}
                className={`flex flex-col items-center transition-all duration-200 hover:scale-105 ${activeStep === 'centralData' ? 'text-blue-600' : (activeStep === 'specificData' || activeStep === 'withdrawalPlan' || activeStep === 'impact' || activeStep === 'advantages' || activeStep === 'results') ? 'text-blue-400' : 'text-gray-400'}`}
                title="Paso 1: Datos Centrales con Reinversión"
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 ${
                  activeStep === 'centralData' ? 'bg-blue-100 text-blue-600 ring-2 ring-blue-500' : 
                  (activeStep === 'specificData' || activeStep === 'withdrawalPlan' || activeStep === 'impact' || activeStep === 'advantages' || activeStep === 'results') ? 'bg-blue-50 text-blue-500' : 'bg-gray-100'
                }`}>
                  <span className="text-sm font-bold">1</span>
                </div>
                <span className="text-xs mt-1">Datos Centrales</span>
              </button>
              
              {/* Continue with existing steps... */}
              <div className={`h-0.5 w-8 mx-1 ${(activeStep === 'specificData' || activeStep === 'withdrawalPlan' || activeStep === 'impact' || activeStep === 'advantages' || activeStep === 'results') ? 'bg-blue-500' : 'bg-gray-200'}`}></div>
              
              <button 
                onClick={() => setActiveStep('specificData')}
                className={`flex flex-col items-center transition-all duration-200 hover:scale-105 ${activeStep === 'specificData' ? 'text-blue-600' : (activeStep === 'withdrawalPlan' || activeStep === 'impact' || activeStep === 'advantages' || activeStep === 'results') ? 'text-blue-400' : 'text-gray-400'}`}
                title="Paso 2: Configuración de Reinversión"
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 ${
                  activeStep === 'specificData' ? 'bg-blue-100 text-blue-600 ring-2 ring-blue-500' : 
                  (activeStep === 'withdrawalPlan' || activeStep === 'impact' || activeStep === 'advantages' || activeStep === 'results') ? 'bg-blue-50 text-blue-500' : 'bg-gray-100'
                }`}>
                  <span className="text-sm font-bold">2</span>
                </div>
                <span className="text-xs mt-1">Configuración</span>
              </button>
              
              <div className={`h-0.5 w-8 mx-1 ${(activeStep === 'withdrawalPlan' || activeStep === 'impact' || activeStep === 'advantages' || activeStep === 'results') ? 'bg-blue-500' : 'bg-gray-200'}`}></div>
              
              <button 
                onClick={() => setActiveStep('withdrawalPlan')}
                className={`flex flex-col items-center transition-all duration-200 hover:scale-105 ${activeStep === 'withdrawalPlan' ? 'text-blue-600' : (activeStep === 'results' || activeStep === 'impact' || activeStep === 'advantages') ? 'text-blue-400' : 'text-gray-400'}`}
                title="Paso 3: Plan de Retiros Personalizado"
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 ${
                  activeStep === 'withdrawalPlan' ? 'bg-blue-100 text-blue-600 ring-2 ring-blue-500' : 
                  (activeStep === 'results' || activeStep === 'impact' || activeStep === 'advantages') ? 'bg-blue-50 text-blue-500' : 'bg-gray-100'
                }`}>
                  <span className="text-sm font-bold">3</span>
                </div>
                <span className="text-xs mt-1">Plan de Retiros</span>
              </button>
              
              <div className={`h-0.5 w-8 mx-1 ${(activeStep === 'results' || activeStep === 'impact' || activeStep === 'advantages') ? 'bg-blue-500' : 'bg-gray-200'}`}></div>
              
              <button 
                onClick={() => setActiveStep('results')}
                className={`flex flex-col items-center transition-all duration-200 hover:scale-105 ${activeStep === 'results' ? 'text-blue-600' : (activeStep === 'impact' || activeStep === 'advantages') ? 'text-blue-400' : 'text-gray-400'}`}
                title="Paso 4: Resultados y Análisis"
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 ${
                  activeStep === 'results' ? 'bg-blue-100 text-blue-600 ring-2 ring-blue-500' : 
                  (activeStep === 'impact' || activeStep === 'advantages') ? 'bg-blue-50 text-blue-500' : 'bg-gray-100'
                }`}>
                  <span className="text-sm font-bold">4</span>
                </div>
                <span className="text-xs mt-1">Resultados</span>
              </button>
              
              <div className={`h-0.5 w-8 mx-1 ${(activeStep === 'impact' || activeStep === 'advantages') ? 'bg-blue-500' : 'bg-gray-200'}`}></div>
              
              <button 
                onClick={() => setActiveStep('impact')}
                className={`flex flex-col items-center transition-all duration-200 hover:scale-105 ${activeStep === 'impact' ? 'text-blue-600' : (activeStep === 'advantages') ? 'text-blue-400' : 'text-gray-400'}`}
                title="Paso 5: Impacto Amplificado"
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 ${
                  activeStep === 'impact' ? 'bg-blue-100 text-blue-600 ring-2 ring-blue-500' : 
                  (activeStep === 'advantages') ? 'bg-blue-50 text-blue-500' : 'bg-gray-100'
                }`}>
                  <span className="text-sm font-bold">5</span>
                </div>
                <span className="text-xs mt-1">Impacto</span>
              </button>
              
              <div className={`h-0.5 w-8 mx-1 ${(activeStep === 'advantages') ? 'bg-blue-500' : 'bg-gray-200'}`}></div>
              
              <button 
                onClick={() => setActiveStep('advantages')}
                className={`flex flex-col items-center transition-all duration-200 hover:scale-105 ${activeStep === 'advantages' ? 'text-blue-600' : 'text-gray-400'}`}
                title="Paso 6: Propuesta de Valor Única"
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 ${
                  activeStep === 'advantages' ? 'bg-blue-100 text-blue-600 ring-2 ring-blue-500' : 'bg-gray-100'
                }`}>
                  <span className="text-sm font-bold">6</span>
                </div>
                <span className="text-xs mt-1">Ventajas</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <motion.div 
          key={activeStep}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.5 }}
        >
          {renderStepContent()}
        </motion.div>
      </div>
    </div>
  );
};

export default ReinvestmentCalculator;